/**
 * YouTube Iframe Resize Hook
 * Resizes YouTube iframes to fit container width while maintaining aspect ratio
 */
import { useEffect, useRef } from 'react';

export const useYouTubeIframeResize = (contentRef, content) => {
  const resizeTimerRef = useRef(null);

  useEffect(() => {
    if (!contentRef.current || !content) return;

    const resizeYouTubeIframes = () => {
      const container = contentRef.current;
      if (!container) return;

      const iframes = container.querySelectorAll('iframe[src*="youtube.com/embed"]');
      iframes.forEach((iframe) => {
        const src = iframe.getAttribute('src') || '';
        const dataShorts = iframe.getAttribute('data-youtube-shorts');
        const dataType = iframe.getAttribute('data-youtube-type');
        const originalWidth = parseInt(iframe.getAttribute('width')) || 0;
        const originalHeight = parseInt(iframe.getAttribute('height')) || 0;

        // Check if it's Shorts - preserve Shorts dimensions
        const isShorts =
          dataShorts === 'true' ||
          dataShorts === true ||
          dataType === 'shorts' ||
          (originalWidth === 449 && originalHeight === 799);

        if (isShorts) {
          // Shorts: Keep original dimensions
          iframe.style.width = originalWidth + 'px';
          iframe.style.height = originalHeight + 'px';
        } else if (originalWidth > 0 && originalHeight > 0) {
          // Regular YouTube: Calculate responsive size
          // Get container width (or window width as fallback)
          const containerWidth = container.clientWidth || window.innerWidth;
          
          // Only resize if original width exceeds container width
          if (originalWidth > containerWidth) {
            const aspectRatio = originalHeight / originalWidth;

            // Calculate new dimensions based on 100% container width
            const newWidth = containerWidth;
            const newHeight = Math.floor(newWidth * aspectRatio);

            // Set inline style only - keep original width/height attributes unchanged
            iframe.style.width = newWidth + 'px';
            iframe.style.height = newHeight + 'px';
          } else {
            // Remove inline styles if not needed
            iframe.style.width = '';
            iframe.style.height = '';
          }
        }
      });
    };

    // Initial resize
    resizeYouTubeIframes();

    // Resize on window resize
    const handleResize = () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = setTimeout(resizeYouTubeIframes, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
    };
  }, [contentRef, content]);
};

